import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Typography, CircularProgress, Grid } from '@mui/material';
import ImageNotSupportedOutlinedIcon from '@mui/icons-material/ImageNotSupportedOutlined';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import { get, put } from '../../services/https';
import useSnackbar from '../../hooks/useSnackbar';
import { DetailPageHeader } from '../../components/layout/DetailPageHeader';
import { EditIcon } from '../../components/common/EditIcon';
import {
  purchaseStatusColors,
  purchaseStatusLabels,
} from './components/atoms/PurchaseStatusBadge';
import UpdateLeadDialog from './components/molecules/UpdateLeadDialog';
import { formatDate } from '../../utils/helper';

interface PurchaseLeadDetails {
  _id: string;
  purchaseId: string;
  userId: {
    _id: string;
    name: string;
    phoneNumber: string;
    countryCode: string;
    email: string;
    type?: string;
  };
  productId: {
    _id: string;
    name: string;
    brand: string;
    model?: string;
    category?: string;
    images: string[];
    pricing: {
      retailPrice?: number;
      mrp?: number;
      discountedPrice?: number;
      customerPrice?: number;
      contractorPointPrice?: number;
    };
    modelSeries?: string;
    indoorModelNo?: string;
    outdoorModelNo?: string;
    manufacturer?: string;
    specifications?: {
      type?: string;
      acType?: string;
      tonnage?: string | number;
      inverter?: boolean;
      seer?: string | number;
      iseer?: string | number;
      energyLabel?: string;
      annualEnergyConsumptionCooling?: string;
      annualEnergyConsumptionHeating?: string;
      powerConsumption?: string;
      runningCurrent?: string;
      startingCurrent?: string;
      maxFuseSize?: string;
      refrigerant?: string;
      ukTotalCooling?: string;
      ukSensibleCooling?: string;
      nominalCooling?: string;
      nominalHeating?: string;
      warrantyType?: string;
      warranty?: {
        product?: string;
        compressor?: string;
        pcb?: string;
      };
    };
  };
  enquiryId?: {
    _id: string;
    addressDetails?: {
      house?: string;
      street?: string;
      city?: string;
      state?: string;
      zipcode?: string;
    };
  };
  quantity: number;
  unitPrice: number;
  status: 'NEW' | 'CONTACTED' | 'FOLLOW_UP' | 'CONVERTED' | 'LOST';
  source: string;
  isActive: boolean;
  remarks?: string;
  soldAmount?: number;
  createdAt: string;
  updatedAt: string;
}

interface DetailRowProps {
  label: string;
  value: React.ReactNode;
}

const DetailRow: React.FC<DetailRowProps> = ({ label, value }) => (
  <Box sx={{ display: 'flex', mb: 2 }}>
    <Typography sx={{ color: '#8A8D91', flex: 1, fontSize: 13.5 }}>
      {label}
    </Typography>
    <Typography component="div" sx={{ flex: 1.6, color: '#fff', fontSize: 14 }}>
      {value}
    </Typography>
  </Box>
);

const SectionTitle: React.FC<{
  title: string;
  icon?: React.ReactNode;
  action?: React.ReactNode;
}> = ({ title, icon, action }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      px: 3,
      pt: 2.5,
      pb: 1.5,
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: 15 }}>
        {title}
      </Typography>
      {icon}
    </Box>
    {action}
  </Box>
);

const LeadDetailsPage: React.FC = () => {
  const { purchaseLeadId } = useParams<{ purchaseLeadId: string }>();
  const navigate = useNavigate();
  const { showSnackbar, SnackbarComponent } = useSnackbar();

  const [loading, setLoading] = useState(true);
  const [editLoading, setEditLoading] = useState(false);
  const [leadDetails, setLeadDetails] = useState<PurchaseLeadDetails | null>(
    null,
  );
  const [openEditDialog, setOpenEditDialog] = useState(false);

  useEffect(() => {
    if (purchaseLeadId) {
      fetchLeadDetails();
    }
  }, [purchaseLeadId]);

  const fetchLeadDetails = async () => {
    try {
      setLoading(true);
      const response = await get(
        `/api/v1/admin/shop/purchase/lead/${purchaseLeadId}`,
      );

      if (response.data.status && response.data.data) {
        setLeadDetails(response.data.data);
      }
    } catch {
      showSnackbar('Error fetching lead details', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateLead = async (updateData: {
    status: string;
    remarks: string;
    soldAmount: number;
    quantity: number;
  }) => {
    if (!leadDetails) return;

    try {
      setEditLoading(true);
      const response = await put(
        `/api/v1/admin/shop/purchase/lead/update/${leadDetails._id}`,
        updateData,
      );

      if (response.status) {
        showSnackbar('Lead updated successfully!', 'success');
        setOpenEditDialog(false);
        fetchLeadDetails();
      } else {
        showSnackbar(response.message || 'Failed to update lead', 'error');
      }
    } catch (error: any) {
      showSnackbar(
        error.response?.data?.message || 'Error updating lead',
        'error',
      );
    } finally {
      setEditLoading(false);
    }
  };

  const calculateTotal = () => {
    if (!leadDetails) return 0;
    return (leadDetails.unitPrice || 0) * (leadDetails.quantity || 1);
  };

  const formatAddress = () => {
    const address = leadDetails?.enquiryId?.addressDetails;
    if (!address) return '-';
    const parts = [
      address.house,
      address.street,
      address.city,
      address.state,
      address.zipcode,
    ].filter((part) => part && part !== 'NA' && part !== '000000');
    return parts.length ? parts.join(', ') : '-';
  };

  const mapToDialogLead = () => {
    if (!leadDetails) return null;
    return {
      _id: leadDetails._id,
      quantity: leadDetails.quantity,
      unitPrice: leadDetails.unitPrice,
      status: leadDetails.status,
      source: leadDetails.source,
      createdAt: leadDetails.createdAt,
      soldAmount: leadDetails.soldAmount,
      remarks: leadDetails.remarks,
      user: {
        name: leadDetails.userId?.name || 'N/A',
        phoneNumber:
          `${leadDetails.userId?.countryCode || ''} ${leadDetails.userId?.phoneNumber || ''}`.trim(),
      },
      product: {
        name: leadDetails.productId?.name || 'N/A',
        brand: leadDetails.productId?.brand || 'N/A',
        model: '',
        customerPrice: leadDetails.productId?.pricing?.customerPrice,
      },
    };
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
          p: 4,
        }}
      >
        <CircularProgress sx={{ color: '#7B1FA2' }} />
      </Box>
    );
  }

  if (!leadDetails) {
    return (
      <Box p={1} bgcolor="#121212" color="#ffffff" height="100%">
        <DetailPageHeader title="Request Details" />
        <Typography textAlign="center" marginTop="20%" color="#8A8D91">
          Lead not found
        </Typography>
      </Box>
    );
  }

  return (
    <Box bgcolor="#121212" color="#ffffff" height="100%">
      {SnackbarComponent}

      <DetailPageHeader
        title="Request Details"
        actions={<EditIcon size={20} onClick={() => setOpenEditDialog(true)} />}
      />

      <Box sx={{ p: 2, overflowY: 'auto' }}>
        <Box
          sx={{
            background: '#141414',
            border: '1px solid #242424',
            borderRadius: '14px',
            overflow: 'hidden',
            pb: 1,
          }}
        >
          {/* ================= CUSTOMER DETAILS ================= */}
          <SectionTitle
            title="Customer Details"
            icon={
              <PersonOutlineOutlinedIcon
                sx={{ color: '#8A8D91', fontSize: 18 }}
              />
            }
            action={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                <Typography sx={{ color: '#8A8D91', fontSize: 13.5 }}>
                  Status :
                </Typography>
                <Typography
                  sx={{
                    color: purchaseStatusColors[leadDetails.status],
                    fontSize: 13.5,
                    fontWeight: 700,
                  }}
                >
                  {purchaseStatusLabels[leadDetails.status]}
                </Typography>
              </Box>
            }
          />
          <Box
            sx={{
              mx: 3,
              mb: 2.5,
              px: 2.5,
              pt: 2.5,
              pb: 0.5,
              background: '#1b1b1b',
              borderRadius: '10px',
            }}
          >
            <Grid container>
              <Grid item xs={12} md={6} sx={{ pr: { md: 5 } }}>
                <DetailRow
                  label="Full Name"
                  value={
                    leadDetails.userId?._id ? (
                      <Typography
                        component="span"
                        onClick={() =>
                          navigate(`/customers/${leadDetails.userId._id}`)
                        }
                        sx={{
                          color: '#0BE600',
                          fontSize: 14,
                          textDecoration: 'underline',
                          textUnderlineOffset: '3px',
                          cursor: 'pointer',
                          '&:hover': { color: '#4dff45' },
                        }}
                      >
                        {leadDetails.userId?.name || '-'}
                      </Typography>
                    ) : (
                      leadDetails.userId?.name || '-'
                    )
                  }
                />
                <DetailRow
                  label="Customer Type"
                  value={leadDetails.userId?.type || '-'}
                />
                <DetailRow label="Address" value={formatAddress()} />
              </Grid>
              <Grid item xs={12} md={6}>
                <DetailRow
                  label="Contact Number"
                  value={`${leadDetails.userId?.countryCode || '+91'} ${leadDetails.userId?.phoneNumber || '-'}`}
                />
                <DetailRow
                  label="Order Date"
                  value={formatDate(leadDetails.createdAt)}
                />
                <DetailRow label="Total AC" value={leadDetails.quantity || 0} />
              </Grid>
              <Grid item xs={12} md={6} sx={{ pr: { md: 5 } }}>
                <DetailRow label="Purchase ID" value={leadDetails.purchaseId} />
              </Grid>
              <Grid item xs={12} md={6}>
                <DetailRow label="Source" value={leadDetails.source || '-'} />
              </Grid>
              {leadDetails.remarks ? (
                <Grid item xs={12}>
                  <DetailRow label="Remarks" value={leadDetails.remarks} />
                </Grid>
              ) : null}
            </Grid>
          </Box>

          {/* ================= PRODUCT DETAILS ================= */}
          <SectionTitle title="Product Details" />
          <Box
            sx={{
              mx: 3,
              mb: 2.5,
              px: 2.5,
              py: 2.5,
              background: '#1b1b1b',
              borderRadius: '10px',
            }}
          >
            <Grid container spacing={2}>
              <Grid item xs={12} md={7}>
                <DetailRow
                  label="Product Name"
                  value={
                    leadDetails.productId?._id ? (
                      <Typography
                        component="span"
                        onClick={() =>
                          navigate(
                            `/productdetails/${leadDetails.productId._id}`,
                          )
                        }
                        sx={{
                          color: '#0BE600',
                          fontSize: 14,
                          textDecoration: 'underline',
                          textUnderlineOffset: '3px',
                          cursor: 'pointer',
                          '&:hover': { color: '#4dff45' },
                        }}
                      >
                        {leadDetails.productId?.name || '-'}
                      </Typography>
                    ) : (
                      leadDetails.productId?.name || '-'
                    )
                  }
                />
                <DetailRow
                  label="Category"
                  value={leadDetails.productId?.category || '-'}
                />
                <DetailRow
                  label="Brand"
                  value={leadDetails.productId?.brand || '-'}
                />
                <DetailRow
                  label="Model Number"
                  value={leadDetails.productId?.model || '-'}
                />
                <DetailRow
                  label="Model Series"
                  value={leadDetails.productId?.modelSeries || '-'}
                />
                <DetailRow
                  label="Indoor Model No"
                  value={leadDetails.productId?.indoorModelNo || '-'}
                />
                <DetailRow
                  label="Outdoor Model No"
                  value={leadDetails.productId?.outdoorModelNo || '-'}
                />
                <DetailRow
                  label="Manufacturer"
                  value={leadDetails.productId?.manufacturer || '-'}
                />
              </Grid>
              <Grid item xs={12} md={5}>
                {leadDetails.productId?.images?.length > 0 ? (
                  <Box
                    component="img"
                    src={leadDetails.productId.images[0]}
                    alt="product"
                    sx={{
                      width: '100%',
                      maxWidth: 240,
                      height: 130,
                      objectFit: 'contain',
                      borderRadius: '8px',
                      background: '#fff',
                      border: '1px solid #2a2a2a',
                      display: 'block',
                      ml: { md: 'auto' },
                    }}
                  />
                ) : (
                  <Box
                    sx={{
                      width: '100%',
                      maxWidth: 240,
                      height: 130,
                      borderRadius: '8px',
                      border: '1px dashed #3a3a3a',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 1,
                      color: '#666',
                      ml: { md: 'auto' },
                    }}
                  >
                    <ImageNotSupportedOutlinedIcon />
                    <Typography sx={{ fontSize: 12 }}>No image</Typography>
                  </Box>
                )}
              </Grid>
            </Grid>
          </Box>

          {/* ================= PRICING ================= */}
          <SectionTitle title="Pricing" />
          <Box sx={{ mx: 3, mb: 2.5 }}>
            <Box
              sx={{
                border: '1px solid #2a2a2a',
                borderRadius: '10px',
                overflow: 'hidden',
              }}
            >
              <Grid container sx={{ background: '#232323', px: 2.5, py: 1.5 }}>
                {[
                  'Retail Price',
                  'Discount Price',
                  'Customer Price',
                  'Contractor Point Price',
                ].map((label) => (
                  <Grid item xs={3} key={label}>
                    <Typography
                      sx={{ color: '#c9c9c9', fontSize: 13, fontWeight: 600 }}
                    >
                      {label}
                    </Typography>
                  </Grid>
                ))}
              </Grid>
              <Grid container sx={{ background: '#1b1b1b', px: 2.5, py: 2.5 }}>
                {[
                  leadDetails.productId?.pricing?.retailPrice ||
                    leadDetails.productId?.pricing?.mrp,
                  leadDetails.productId?.pricing?.discountedPrice,
                  leadDetails.productId?.pricing?.customerPrice,
                  leadDetails.productId?.pricing?.contractorPointPrice,
                ].map((value, index) => (
                  <Grid item xs={3} key={index}>
                    <Typography sx={{ color: '#fff', fontSize: 15 }}>
                      {value !== undefined && value !== null
                        ? Number(value).toFixed(2)
                        : '-'}
                    </Typography>
                  </Grid>
                ))}
              </Grid>
            </Box>

            <Box sx={{ px: 0.5, pt: 2.5 }}>
              <Grid container>
                <Grid item xs={12} md={6} sx={{ pr: { md: 5 } }}>
                  <DetailRow
                    label="Unit Price"
                    value={`£ ${leadDetails.unitPrice?.toLocaleString() || '0'}`}
                  />
                  <DetailRow
                    label="Quantity"
                    value={leadDetails.quantity || 0}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <DetailRow
                    label="Calculated Total"
                    value={
                      <Typography
                        component="span"
                        sx={{ color: '#0BE600', fontWeight: 700, fontSize: 14 }}
                      >
                        £ {calculateTotal().toLocaleString()}
                      </Typography>
                    }
                  />
                  {leadDetails.soldAmount && leadDetails.soldAmount > 0 ? (
                    <DetailRow
                      label="Sold Amount"
                      value={
                        <Typography
                          component="span"
                          sx={{
                            color: '#ffa726',
                            fontWeight: 700,
                            fontSize: 14,
                          }}
                        >
                          £ {leadDetails.soldAmount.toLocaleString()}
                        </Typography>
                      }
                    />
                  ) : null}
                </Grid>
              </Grid>
            </Box>
          </Box>

          {/* ================= AC DETAILS ================= */}
          <SectionTitle title="AC Details" />
          <Box
            sx={{
              mx: 3,
              mb: 2.5,
              px: 2.5,
              pt: 2.5,
              pb: 0.5,
              background: '#1b1b1b',
              borderRadius: '10px',
            }}
          >
            <Grid container>
              <Grid item xs={12} md={6} sx={{ pr: { md: 5 } }}>
                <DetailRow
                  label="AC Type"
                  value={
                    leadDetails.productId?.specifications?.type ||
                    leadDetails.productId?.specifications?.acType ||
                    '-'
                  }
                />
                <DetailRow
                  label="Tonnage"
                  value={leadDetails.productId?.specifications?.tonnage || '-'}
                />
                <DetailRow
                  label="Inverter Technology"
                  value={
                    leadDetails.productId?.specifications?.inverter ===
                    undefined
                      ? '-'
                      : leadDetails.productId.specifications.inverter
                        ? 'Yes'
                        : 'No'
                  }
                />
                <DetailRow
                  label="SEER"
                  value={
                    leadDetails.productId?.specifications?.seer ||
                    leadDetails.productId?.specifications?.iseer ||
                    '-'
                  }
                />
                <DetailRow
                  label="Energy Label"
                  value={
                    leadDetails.productId?.specifications?.energyLabel || '-'
                  }
                />
                <DetailRow
                  label="Annual Cooling Energy"
                  value={
                    leadDetails.productId?.specifications
                      ?.annualEnergyConsumptionCooling || '-'
                  }
                />
                <DetailRow
                  label="Annual Heating Energy"
                  value={
                    leadDetails.productId?.specifications
                      ?.annualEnergyConsumptionHeating || '-'
                  }
                />
                <DetailRow
                  label="UK Total Cooling"
                  value={
                    leadDetails.productId?.specifications?.ukTotalCooling || '-'
                  }
                />
                <DetailRow
                  label="UK Sensible Cooling"
                  value={
                    leadDetails.productId?.specifications?.ukSensibleCooling ||
                    '-'
                  }
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <DetailRow
                  label="Power Consumption"
                  value={
                    leadDetails.productId?.specifications?.powerConsumption ||
                    '-'
                  }
                />
                <DetailRow
                  label="Refrigerant Type"
                  value={
                    leadDetails.productId?.specifications?.refrigerant || '-'
                  }
                />
                <DetailRow
                  label="Nominal Cooling"
                  value={
                    leadDetails.productId?.specifications?.nominalCooling || '-'
                  }
                />
                <DetailRow
                  label="Nominal Heating"
                  value={
                    leadDetails.productId?.specifications?.nominalHeating || '-'
                  }
                />
                <DetailRow
                  label="Running Current"
                  value={
                    leadDetails.productId?.specifications?.runningCurrent || '-'
                  }
                />
                <DetailRow
                  label="Starting Current"
                  value={
                    leadDetails.productId?.specifications?.startingCurrent ||
                    '-'
                  }
                />
                <DetailRow
                  label="Max Fuse Size"
                  value={
                    leadDetails.productId?.specifications?.maxFuseSize || '-'
                  }
                />
                <DetailRow
                  label="Warranty Type"
                  value={
                    leadDetails.productId?.specifications?.warrantyType || '-'
                  }
                />
                <DetailRow
                  label="Compressor Warranty"
                  value={
                    leadDetails.productId?.specifications?.warranty
                      ?.compressor || '-'
                  }
                />
              </Grid>
            </Grid>
          </Box>
        </Box>
      </Box>

      <UpdateLeadDialog
        open={openEditDialog}
        onClose={() => setOpenEditDialog(false)}
        onSubmit={handleUpdateLead}
        lead={mapToDialogLead()}
        loading={editLoading}
      />
    </Box>
  );
};

export default LeadDetailsPage;
